import {
  CartForm,
  CollectionGrid,
  DividerPhoto,
  Grid,
  GridSize,
  Hero,
  ProductHero,
  ReviewWidget,
  Video,
  Marquee,
  BlogPostGrid,
  BlogPostHeader,
  BlogCategoryPostGrid,
  BlogAuthorPostGrid,
} from '@data/sanity/schema'
import { SanityBlocks } from './blocks'
import {
  SanityBlogAuthor,
  SanityBlogCategory,
  SanityBlogPostOptions,
  SanityBlogPostWithoutBody,
} from './blog'
import { SanityComplexPortableText } from './content'
import { SanityFilter, SanitySort } from './filter'
import { SanityImageFragment } from './image'
import { SanityProductFragment } from './product'
import {
  SanityCollectionStrings,
  SanityPrintConfiguratorStrings,
  SanityReviewsStrings,
} from './strings'
import { SanityMuxVideo, SanityVideo } from './video'

export enum SanityModuleType {
  GRID = 'grid',
  HERO = 'hero',
  DIVIDER_PHOTO = 'dividerPhoto',
  PRODUCT_HERO = 'productHero',
  COLLECTION_GRID = 'collectionGrid',
  REVIEW_WIDGET = 'reviewWidget',
  CART_FORM = 'cartForm',
  VIDEO = 'video',
  MARQUEE = 'marquee',
  BLOG_POST_GRID = 'blogPostGrid',
  BLOG_POST_HEADER = 'blogPostHeader',
  BLOG_CATEGORY_POST_GRID = 'blogCategoryPostGrid',
  BLOG_AUTHOR_POST_GRID = 'blogAuthorPostGrid',
}

export type SanityGridBackground =
  | 'gradient'
  | 'gray-lightest'
  | 'gray'
  | 'dark-gray'
  | 'green'
  | 'dark-green'
  | 'red'
  | 'dark-red'

// Module component types
export type SanityGridColumnSize = Pick<
  GridSize,
  'breakpoint' | 'width' | 'justify' | 'align' | 'start'
>

export interface SanityGridColumn {
  _key: string
  sizes: SanityGridColumnSize[]
  blocks: SanityBlocks[]
  style: string
}

export type SanityGrid = Pick<
  Grid,
  'size' | 'style' | 'noColumnGaps' | 'noRowGaps' | 'reverseSequence'
> & {
  background?: SanityGridBackground
  columns: SanityGridColumn[]
}

export interface SanityHeroPhotos {
  mobilePhoto?: SanityImageFragment
  desktopPhoto?: SanityImageFragment
}

export enum SanityHeroBackgroundType {
  PHOTO = 'photo',
  MUX_VIDEO = 'mux-video',
}

export enum SanityHeroContentPosition {
  CENTER = 'center',
  BOTTOM_LEFT = 'bottom-left',
}

export interface SanityHero {
  bgType: SanityHeroBackgroundType
  contentPosition: SanityHeroContentPosition
  content?: SanityComplexPortableText
  photos?: SanityHeroPhotos
  muxVideo?: SanityMuxVideo
}

export interface SanityDividerPhoto {
  photo: SanityImageFragment
}

export type SanityCollectionGrid = Pick<CollectionGrid, 'active'> & {
  paginationLimit: number
  title?: string
  filter?: SanityFilter
  sort?: SanitySort
}

export enum SanityReviewWidgetType {
  MAIN = 'main',
  PRODUCT = 'product',
}

export type SanityReviewWidget = {
  type: SanityReviewWidgetType
}

export type SanityCartForm = Pick<CartForm, 'active'>

export interface SanityMarqueeSimpleItem {
  _key: string
  _type: 'simple'
  text: string
}

export interface SanityMarqueePhotoItem {
  _key: string
  _type: 'photo'
  photo: SanityImageFragment
}

export interface SanityMarqueeProductItem {
  _key: string
  _type: 'product'
  _id: string
  product: SanityProductFragment
}

export type SanityMarqueeItem =
  | SanityMarqueeSimpleItem
  | SanityMarqueePhotoItem
  | SanityMarqueeProductItem

export type SanityMarquee = Pick<Marquee, 'speed' | 'reverse' | 'pausable'> & {
  items: SanityMarqueeItem[]
}

export interface SanityBlogPostGrid {
  posts: SanityBlogPostWithoutBody[]
  author?: SanityBlogAuthor
  category?: SanityBlogCategory
  options?: SanityBlogPostOptions
}

export interface SanityBlogPostHeader {
  post?: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
}

export type SanityBlogCategoryPostGrid = Pick<SanityBlogPostGrid, 'options'>

export type SanityBlogAuthorPostGrid = Pick<SanityBlogPostGrid, 'options'>

// Module raw data types
export type SanityRawModule =
  | BlogPostGrid
  | BlogPostHeader
  | CartForm
  | CollectionGrid
  | DividerPhoto
  | Grid
  | Hero
  | Marquee
  | ProductHero
  | ReviewWidget
  | Video

// Module data types
export type SanityGridModule = Pick<Grid, '_type'> &
  SanityGrid & {
    _key: string
  }

export type SanityHeroModule = Pick<Hero, '_type'> &
  SanityHero & {
    _key: string
  }

export type SanityDividerPhotoModule = Pick<DividerPhoto, '_type'> &
  SanityDividerPhoto & {
    _key: string
  }

export type SanityProductHeroModule = Pick<ProductHero, '_type'> & {
  _key: string
  printConfiguratorStrings: SanityPrintConfiguratorStrings
}

export type SanityCollectionGridModule = Pick<CollectionGrid, '_type'> &
  SanityCollectionGrid & {
    _key: string
    collectionStrings: SanityCollectionStrings
  }

export type SanityReviewWidgetModule = Pick<ReviewWidget, '_type'> &
  SanityReviewWidget & {
    _key: string
    reviewsStrings: SanityReviewsStrings
  }

export type SanityCartFormModule = Pick<CartForm, '_type'> &
  SanityCartForm & {
    _key: string
  }

export type SanityVideoModule = Pick<Video, '_type'> &
  SanityVideo & {
    _key: string
  }

export type SanityMarqueeModule = Pick<Marquee, '_type'> &
  SanityMarquee & {
    _key: string
  }

export type SanityBlogPostGridModule = Pick<BlogPostGrid, '_type'> &
  SanityBlogPostGrid & {
    _key: string
  }

export type SanityBlogPostHeaderModule = Pick<BlogPostHeader, '_type'> &
  SanityBlogPostHeader & {
    _key: string
  }

export type SanityBlogCategoryPostGridModule = Pick<
  BlogCategoryPostGrid,
  '_type'
> &
  SanityBlogCategoryPostGrid & {
    _key: string
  }

export type SanityBlogAuthorPostGridModule = Pick<BlogAuthorPostGrid, '_type'> &
  SanityBlogAuthorPostGrid & {
    _key: string
  }

export type SanityModule =
  | SanityGridModule
  | SanityHeroModule
  | SanityDividerPhotoModule
  | SanityProductHeroModule
  | SanityCollectionGridModule
  | SanityReviewWidgetModule
  | SanityCartFormModule
  | SanityVideoModule
  | SanityMarqueeModule
  | SanityBlogPostGridModule
  | SanityBlogPostHeaderModule
  | SanityBlogCategoryPostGridModule
  | SanityBlogAuthorPostGridModule
